<template>
    <main class="fb-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/forms" class="tbv2-crumb">{{ $t('menu.forms.form') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ heroTitle }}</span>
            </div>
        </portal>

        <header class="fb-hero">
            <router-link to="/forms" class="fb-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <h1 class="fb-hero-title">{{ heroTitle }}</h1>
            <p class="fb-hero-sub">{{ heroSubtitle }}</p>
        </header>

        <div class="fb-wrapper">
            <!-- Loading -->
            <div v-if="loading" class="fb-loading">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">{{ $t('forms.loading') }}...</span>
                </div>
            </div>

            <!-- Stepper -->
            <div class="fb-stepper">
                <div
                    v-for="(s, index) in steps"
                    :key="index"
                    class="fb-stepper-item"
                    :class="{
                        'is-active': step === index + 1,
                        'is-done': step > index + 1
                    }"
                    @click="goToStep(index + 1)"
                >
                    <div class="fb-stepper-icon">
                        <i v-if="step > index + 1" class="fal fa-check"></i>
                        <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div class="fb-stepper-text">
                        <span class="fb-stepper-label">{{ s.label }}</span>
                        <span class="fb-stepper-desc">{{ s.description }}</span>
                    </div>
                    <div v-if="index < steps.length - 1" class="fb-stepper-line"></div>
                </div>
            </div>

            <!-- Content -->
            <div class="fb-content" :class="{ 'fb-content--builder': step === 3 }">
                <transition appear mode="out-in" name="slide-fade">
                    <!-- Step 1 : Type de formulaire -->
                    <div v-if="step === 1" key="step1" class="fb-panel">
                        <div class="fb-type-select">
                            <div class="fb-section-header">
                                <div class="fb-section-icon">
                                    <i class="fal fa-layer-group"></i>
                                </div>
                                <div>
                                    <h3 class="fb-section-title">{{ $t('form_builder.config.form_type') }}</h3>
                                    <p class="fb-section-desc">{{ $t('form_builder.config.form_type_placeholder') }}</p>
                                </div>
                            </div>

                            <div v-if="formTypes.length === 0" class="fb-type-empty">
                                <i class="fal fa-spinner-third fa-spin"></i>
                            </div>

                            <div v-else class="fb-type-grid">
                                <div
                                    v-for="ft in formTypes"
                                    :key="ft.id"
                                    class="fb-type-card"
                                    :class="{ 'is-selected': selectedFormTypeId === ft.id }"
                                    @click="selectedFormTypeId = ft.id"
                                >
                                    <div class="fb-type-card-icon">
                                        <i class="fal fa-layer-group"></i>
                                    </div>
                                    <div class="fb-type-card-info">
                                        <span class="fb-type-card-name">{{ ft.name }}</span>
                                        <span v-if="ft.description" class="fb-type-card-desc">{{ ft.description }}</span>
                                    </div>
                                    <div v-if="selectedFormTypeId === ft.id" class="fb-type-card-check">
                                        <i class="fal fa-check-circle"></i>
                                    </div>
                                </div>
                            </div>

                            <span v-if="submitted && !selectedFormTypeId" class="fb-error" style="margin-top: 12px;">
                                {{ $t('form_builder.validation.required') }}
                            </span>
                        </div>
                    </div>

                    <!-- Step 2 : Informations generales -->
                    <div v-else-if="step === 2" key="step2" class="fb-panel">
                        <div class="fb-step1-layout">
                            <!-- Left: General info -->
                            <div class="fb-step1-left">
                                <div class="fb-section-header">
                                    <div class="fb-section-icon">
                                        <i class="fal fa-info-circle"></i>
                                    </div>
                                    <div>
                                        <h3 class="fb-section-title">{{ $t('post.page.information.general') }}</h3>
                                    </div>
                                </div>

                                <!-- Language tabs -->
                                <div class="fb-lang-tabs">
                                    <button
                                        v-for="lang in langs"
                                        :key="lang.code"
                                        class="fb-lang-tab"
                                        :class="{ 'is-active': activeLang === lang.code }"
                                        @click="activeLang = lang.code"
                                    >
                                        <span class="fb-lang-flag">{{ lang.flag }}</span>
                                        {{ lang.label }}
                                    </button>
                                </div>

                                <!-- FR -->
                                <div v-show="activeLang === 'fr'" class="fb-fields">
                                    <div class="fb-field" :class="{ 'has-error': submitted && !formData.fr.name }">
                                        <label class="fb-label">
                                            {{ $t('post.page.preview.form_name') }} <span class="fb-required">*</span>
                                        </label>
                                        <input
                                            v-model="formData.fr.name"
                                            type="text"
                                            class="fb-input"
                                            :placeholder="$t('post.page.preview.form_name')"
                                        />
                                        <span v-if="submitted && !formData.fr.name" class="fb-error">
                                            {{ $t('form_builder.validation.required') }}
                                        </span>
                                    </div>

                                    <div class="fb-field" :class="{ 'has-error': submitted && !formData.fr.description }">
                                        <label class="fb-label">
                                            {{ $t('post.page.preview.description') }} <span class="fb-required">*</span>
                                        </label>
                                        <ckeditor
                                            v-model="formData.fr.description"
                                            :editor="editor"
                                            :config="editorConfig"
                                        ></ckeditor>
                                        <span v-if="submitted && !formData.fr.description" class="fb-error">
                                            {{ $t('form_builder.validation.required') }}
                                        </span>
                                    </div>
                                </div>

                                <!-- EN -->
                                <div v-show="activeLang === 'en'" class="fb-fields">
                                    <div class="fb-field" :class="{ 'has-error': submitted && !formData.en.name }">
                                        <label class="fb-label">
                                            {{ $t('post.page.preview.form_name') }} <span class="fb-required">*</span>
                                        </label>
                                        <input
                                            v-model="formData.en.name"
                                            type="text"
                                            class="fb-input"
                                            :placeholder="$t('post.page.preview.form_name')"
                                        />
                                        <span v-if="submitted && !formData.en.name" class="fb-error">
                                            {{ $t('form_builder.validation.required') }}
                                        </span>
                                    </div>

                                    <div class="fb-field" :class="{ 'has-error': submitted && !formData.en.description }">
                                        <label class="fb-label">
                                            {{ $t('post.page.preview.description') }} <span class="fb-required">*</span>
                                        </label>
                                        <ckeditor
                                            v-model="formData.en.description"
                                            :editor="editor"
                                            :config="editorConfig"
                                        ></ckeditor>
                                        <span v-if="submitted && !formData.en.description" class="fb-error">
                                            {{ $t('form_builder.validation.required') }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Right: RACI -->
                            <div class="fb-step1-right">
                                <div class="fb-section-header">
                                    <div class="fb-section-icon">
                                        <i class="fal fa-users"></i>
                                    </div>
                                    <div>
                                        <h3 class="fb-section-title">{{ $t('post.page.racis.title') }}</h3>
                                        <p class="fb-section-desc">{{ $t('post.page.racis.description') }}</p>
                                    </div>
                                </div>

                                <div class="fb-raci-grid">
                                    <!-- Operateur (Responsible) -->
                                    <div class="fb-raci-card fb-raci--blue">
                                        <div class="fb-raci-card-header">
                                            <div class="fb-raci-card-icon">
                                                <i class="fal fa-user-cog"></i>
                                            </div>
                                            <span class="fb-raci-card-title">{{ $t('post.page.racis.is_responsible') }}</span>
                                        </div>
                                        <v-select
                                            v-model="racis.responsible"
                                            :filterable="false"
                                            :options="userOptions"
                                            label="name"
                                            multiple
                                            class="fb-vselect"
                                            @search="onUserSearch"
                                        >
                                            <template slot="no-options">
                                                {{ $t('post.page.racis.searchuser') }}
                                            </template>
                                        </v-select>
                                    </div>

                                    <!-- Responsable (Accountable) -->
                                    <div class="fb-raci-card fb-raci--purple">
                                        <div class="fb-raci-card-header">
                                            <div class="fb-raci-card-icon">
                                                <i class="fal fa-user-shield"></i>
                                            </div>
                                            <span class="fb-raci-card-title">{{ $t('post.page.racis.is_accountable') }}</span>
                                        </div>
                                        <v-select
                                            v-model="racis.accountable"
                                            :filterable="false"
                                            :options="userOptions"
                                            label="name"
                                            multiple
                                            class="fb-vselect"
                                            @search="onUserSearch"
                                        >
                                            <template slot="no-options">
                                                {{ $t('post.page.racis.searchuser') }}
                                            </template>
                                        </v-select>
                                    </div>

                                    <!-- Consulte -->
                                    <div class="fb-raci-card fb-raci--amber">
                                        <div class="fb-raci-card-header">
                                            <div class="fb-raci-card-icon">
                                                <i class="fal fa-comments"></i>
                                            </div>
                                            <span class="fb-raci-card-title">{{ $t('post.page.racis.editconsulted') }}</span>
                                        </div>
                                        <v-select
                                            v-model="racis.consulted"
                                            :filterable="false"
                                            :options="userOptions"
                                            label="name"
                                            multiple
                                            class="fb-vselect"
                                            @search="onUserSearch"
                                        >
                                            <template slot="no-options">
                                                {{ $t('post.page.racis.searchuser') }}
                                            </template>
                                        </v-select>
                                    </div>

                                    <!-- Informe -->
                                    <div class="fb-raci-card fb-raci--teal">
                                        <div class="fb-raci-card-header">
                                            <div class="fb-raci-card-icon">
                                                <i class="fal fa-bell"></i>
                                            </div>
                                            <span class="fb-raci-card-title">{{ $t('post.page.racis.editinformed') }}</span>
                                        </div>
                                        <v-select
                                            v-model="racis.informed"
                                            :filterable="false"
                                            :options="userOptions"
                                            label="name"
                                            multiple
                                            class="fb-vselect"
                                            @search="onUserSearch"
                                        >
                                            <template slot="no-options">
                                                {{ $t('post.page.racis.searchuser') }}
                                            </template>
                                        </v-select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Step 3 : Etapes & Attributs -->
                    <div v-else-if="step === 3" key="step3" class="fb-panel">
                        <div class="fb-builder-layout">
                            <form-builder-sidebar />
                            <form-builder-canvas
                                :steps="formSteps"
                                @select-field="onSelectField"
                                @select-step="onSelectStep"
                            />
                            <form-builder-field-config
                                :field="selectedField"
                                :known-users="allKnownUsers"
                            />
                        </div>
                    </div>

                    <!-- Step 4 : Sauvegarder -->
                    <div v-else-if="step === 4 && !saved" key="step4" class="fb-panel">
                        <div class="fb-summary">
                            <div class="fb-summary-header">
                                <div class="fb-summary-header-icon">
                                    <i class="fal fa-clipboard-check"></i>
                                </div>
                                <div>
                                    <h3 class="fb-summary-title">{{ $t('form_builder.summary.title') }}</h3>
                                    <p class="fb-summary-subtitle">{{ $t('form_builder.summary.subtitle') }}</p>
                                </div>
                            </div>

                            <div class="fb-summary-grid">
                                <!-- General info card -->
                                <div class="fb-summary-card">
                                    <div class="fb-summary-card-header">
                                        <i class="fal fa-info-circle"></i>
                                        <span>{{ $t('post.page.information.general') }}</span>
                                    </div>
                                    <div class="fb-summary-card-body">
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.preview.form_name') }} (FR)</span>
                                            <span class="fb-summary-value">{{ formData.fr.name || '—' }}</span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.preview.form_name') }} (EN)</span>
                                            <span class="fb-summary-value">{{ formData.en.name || '—' }}</span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.preview.description') }} (FR)</span>
                                            <span v-if="formData.fr.description" class="fb-summary-value fb-summary-value--html" v-html="formData.fr.description"></span>
                                            <span v-else class="fb-summary-value">—</span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.preview.description') }} (EN)</span>
                                            <span v-if="formData.en.description" class="fb-summary-value fb-summary-value--html" v-html="formData.en.description"></span>
                                            <span v-else class="fb-summary-value">—</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- RACI card -->
                                <div class="fb-summary-card">
                                    <div class="fb-summary-card-header">
                                        <i class="fal fa-users"></i>
                                        <span>{{ $t('post.page.racis.title') }}</span>
                                    </div>
                                    <div class="fb-summary-card-body">
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.racis.is_responsible') }}</span>
                                            <span class="fb-summary-value">
                                                <span v-if="racis.responsible.length" class="fb-summary-pills">
                                                    <span v-for="u in racis.responsible" :key="u.id" class="fb-summary-pill fb-pill--blue">{{ u.name }}</span>
                                                </span>
                                                <span v-else class="fb-summary-empty">—</span>
                                            </span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.racis.is_accountable') }}</span>
                                            <span class="fb-summary-value">
                                                <span v-if="racis.accountable.length" class="fb-summary-pills">
                                                    <span v-for="u in racis.accountable" :key="u.id" class="fb-summary-pill fb-pill--purple">{{ u.name }}</span>
                                                </span>
                                                <span v-else class="fb-summary-empty">—</span>
                                            </span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.racis.editconsulted') }}</span>
                                            <span class="fb-summary-value">
                                                <span v-if="racis.consulted.length" class="fb-summary-pills">
                                                    <span v-for="u in racis.consulted" :key="u.id" class="fb-summary-pill fb-pill--amber">{{ u.name }}</span>
                                                </span>
                                                <span v-else class="fb-summary-empty">—</span>
                                            </span>
                                        </div>
                                        <div class="fb-summary-row">
                                            <span class="fb-summary-label">{{ $t('post.page.racis.editinformed') }}</span>
                                            <span class="fb-summary-value">
                                                <span v-if="racis.informed.length" class="fb-summary-pills">
                                                    <span v-for="u in racis.informed" :key="u.id" class="fb-summary-pill fb-pill--teal">{{ u.name }}</span>
                                                </span>
                                                <span v-else class="fb-summary-empty">—</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Steps & fields card (full width) -->
                                <div class="fb-summary-card fb-summary-card--full">
                                    <div class="fb-summary-card-header">
                                        <i class="fal fa-layer-group"></i>
                                        <span>{{ $t('forms.steps_attributes') }}</span>
                                        <span class="fb-summary-badge">{{ formSteps.length }} {{ formSteps.length > 1 ? 'étapes' : 'étape' }}</span>
                                    </div>
                                    <div class="fb-summary-card-body" v-if="formSteps.length">
                                        <div
                                            v-for="(s, si) in formSteps"
                                            :key="si"
                                            class="fb-summary-step"
                                        >
                                            <div class="fb-summary-step-header">
                                                <span class="fb-summary-step-num">{{ si + 1 }}</span>
                                                <span class="fb-summary-step-name">{{ s.name || $t('form_builder.canvas.step_name') }}</span>
                                                <span class="fb-summary-step-count">{{ s.attributes.length }} {{ $t('form_builder.canvas.fields') }}</span>
                                            </div>
                                            <div v-if="s.attributes.length" class="fb-summary-fields">
                                                <span
                                                    v-for="(f, fi) in s.attributes"
                                                    :key="fi"
                                                    class="fb-summary-field-chip"
                                                >
                                                    {{ f.label || $t('form_builder.canvas.untitled_field') }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="fb-summary-card-empty">
                                        {{ $t('form_builder.canvas.empty_title') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Step 4 : Success -->
                    <div v-else-if="step === 4 && saved" key="step4-saved" class="fb-panel">
                        <div class="fb-success">
                            <div class="fb-success-icon">
                                <i class="fal fa-check"></i>
                            </div>
                            <h3 class="fb-success-title">{{ $t('forms.saved') }}</h3>
                            <router-link to="/forms" class="fb-btn fb-btn--primary" style="margin-top: 24px; text-decoration: none;">
                                <i class="fal fa-arrow-left mr-2"></i>
                                {{ $t('forms.back') }}
                            </router-link>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Navigation -->
            <div v-if="!saved" class="fb-nav">
                <button
                    v-if="step > 1"
                    class="fb-btn fb-btn--secondary"
                    @click="step--"
                >
                    <i class="fal fa-arrow-left mr-2"></i>
                    {{ $t('forms.previous') }}
                </button>
                <span v-else></span>

                <button
                    v-if="step < 4"
                    class="fb-btn fb-btn--primary"
                    @click="nextStep"
                >
                    {{ $t('forms.next') }}
                    <i class="fal fa-arrow-right ml-2"></i>
                </button>
                <button
                    v-else
                    class="fb-btn fb-btn--success"
                    :disabled="loading"
                    @click="doSave"
                >
                    <i class="fal fa-save mr-2"></i>
                    {{ $t('forms.save_form') }}
                </button>
            </div>
        </div>
    </main>
</template>

<style>
.fb-field .ck.ck-editor__editable_inline {
    min-height: 120px;
    border-radius: 0 0 10px 10px !important;
}

.fb-field .ck.ck-toolbar {
    border-radius: 10px 10px 0 0 !important;
}

.fb-field.has-error .ck.ck-editor__editable_inline {
    border-color: #ef4444 !important;
}
</style>

<script>
import { mapActions, mapState } from 'vuex'
import axios from 'axios'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import FormBuilderSidebar from '@/components/FormBuilder/FormBuilderSidebar.vue'
import FormBuilderCanvas from '@/components/FormBuilder/FormBuilderCanvas.vue'
import FormBuilderFieldConfig from '@/components/FormBuilder/FormBuilderFieldConfig.vue'

export default {
    name: 'CustomFormBuilder',
    components: {
        FormBuilderSidebar,
        FormBuilderCanvas,
        FormBuilderFieldConfig
    },
    data() {
        return {
            step: 1,
            activeLang: 'fr',
            submitted: false,
            loading: false,
            saved: false,
            userOptions: [],
            langs: [
                { code: 'fr', label: 'Français', flag: '🇫🇷' },
                { code: 'en', label: 'English', flag: '🇬🇧' }
            ],
            steps: [
                { label: this.$t('form_builder.config.form_type'), description: 'Choisir le type' },
                { label: this.$t('forms.form'), description: 'Nom, description, opérateurs' },
                { label: this.$t('forms.steps_attributes'), description: 'Champs du formulaire' },
                { label: this.$t('forms.save'), description: 'Vérifier et enregistrer' }
            ],
            formData: {
                fr: { name: '', description: '' },
                en: { name: '', description: '' }
            },
            racis: {
                responsible: [],
                accountable: [],
                consulted: [],
                informed: []
            },
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'bold',
                        'italic',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo'
                    ]
                }
            },
            formTypes: [],
            selectedFormTypeId: null,
            formSteps: [],
            selectedField: null,
            loadedFormUsers: [],
            hasChanges: false,
            watchActive: false
        }
    },
    computed: {
        ...mapState('form', {
            storeForm: (state) => state.newForm
        }),
        formTypeId() {
            return parseInt(this.$route.query.form_type_id) || null
        },
        formId() {
            return this.$route.params.formId || null
        },
        isEditMode() {
            return !!this.formId
        },
        heroTitle() {
            return this.isEditMode
                ? this.$t('form_builder.hero.edit_title')
                : this.$t('form_builder.hero.create_title')
        },
        heroSubtitle() {
            return this.isEditMode
                ? this.$t('form_builder.hero.edit_subtitle')
                : this.$t('form_builder.hero.create_subtitle')
        },
        step1Valid() {
            return !!this.selectedFormTypeId
        },
        step2Valid() {
            return (
                this.formData.fr.name.trim() &&
                this.formData.fr.description.trim() &&
                this.formData.en.name.trim() &&
                this.formData.en.description.trim()
            )
        },
        step3Valid() {
            return this.formSteps.length > 0
        },
        allKnownUsers() {
            const map = {}
            const addUsers = (arr) => {
                if (!arr) return
                arr.forEach((u) => { if (u && u.id && u.name) map[u.id] = { id: u.id, name: u.name } })
            }
            addUsers(this.racis.responsible)
            addUsers(this.racis.accountable)
            addUsers(this.racis.consulted)
            addUsers(this.racis.informed)
            addUsers(this.userOptions)
            addUsers(this.loadedFormUsers)
            return Object.values(map)
        }
    },
    watch: {
        selectedFormTypeId() {
            if (this.watchActive) this.hasChanges = true
        },
        formData: {
            handler() { if (this.watchActive) this.hasChanges = true },
            deep: true
        },
        racis: {
            handler() { if (this.watchActive) this.hasChanges = true },
            deep: true
        },
        formSteps: {
            handler() { if (this.watchActive) this.hasChanges = true },
            deep: true
        }
    },
    beforeRouteLeave(to, from, next) {
        if (!this.hasChanges || this.saved) {
            next()
            return
        }
        this.$bmvDialog.confirm({
            variant: 'warning',
            title: this.$t('form_builder.unsaved_changes.title'),
            message: this.$t('form_builder.unsaved_changes.message'),
            confirmLabel: this.$t('form_builder.unsaved_changes.confirm'),
            cancelLabel: this.$t('form_builder.unsaved_changes.cancel')
        }).then((confirmed) => {
            if (confirmed) next()
            else next(false)
        })
    },
    mounted() {
        window.addEventListener('beforeunload', this._onBeforeUnload)
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this._onBeforeUnload)
    },
    async created() {
        // Load form types + all users in parallel
        try {
            const [typesResp, usersResp] = await Promise.all([
                this.getFormTypes(),
                this.fetchAllUsers()
            ])
            const types = typesResp.data.types || typesResp.data
            this.formTypes = Array.isArray(types) ? types.filter((t) => t.active) : []
            this.loadedFormUsers = usersResp
        } catch (e) {
            console.error(e)
        }

        if (this.isEditMode) {
            this.loading = true
            try {
                const form = await this.editForm(this.formId)
                this.loadFormFromDB(form)
                this.selectedFormTypeId = form.form_type_id || null
                this.step = 2 // Skip type selection in edit mode
            } catch (e) {
                this.$store.dispatch('dispatchError', e)
            } finally {
                this.loading = false
            }
        } else if (this.formTypeId) {
            await this.initForm()
            this.selectedFormTypeId = this.formTypeId
            this.step = 2 // Type already chosen, skip to info
        }

        this.$nextTick(() => { this.watchActive = true })
    },
    methods: {
        _onBeforeUnload(e) {
            if (this.hasChanges && !this.saved) {
                e.preventDefault()
                e.returnValue = ''
            }
        },

        ...mapActions('auth', {
            searchUser: 'searchUser'
        }),
        ...mapActions('form', {
            createForm: 'createForm',
            updateForm: 'updateForm',
            editForm: 'editForm',
            initForm: 'initForm',
            getFormTypes: 'getFormTypes'
        }),

        // -- Navigation --
        goToStep(n) {
            if (n > 1 && !this.step1Valid) {
                this.submitted = true
                return
            }
            if (n > 2 && !this.step2Valid) {
                this.submitted = true
                this.highlightMissingLang()
                return
            }
            if (n > 3 && !this.step3Valid) {
                this.$store.dispatch('toast', {
                    type: 'error',
                    title: this.$t('error.title'),
                    message: this.$t('form_builder.validation.min_one_step')
                })
                return
            }
            this.submitted = false
            this.step = n
        },
        nextStep() {
            if (this.step === 1) {
                this.submitted = true
                if (!this.step1Valid) return
                this.submitted = false
            }
            if (this.step === 2) {
                this.submitted = true
                if (!this.step2Valid) {
                    this.highlightMissingLang()
                    return
                }
                this.submitted = false
            }
            if (this.step === 3 && !this.step3Valid) {
                this.$store.dispatch('toast', {
                    type: 'error',
                    title: this.$t('error.title'),
                    message: this.$t('form_builder.validation.min_one_step')
                })
                return
            }
            this.step++
        },
        highlightMissingLang() {
            if (!this.formData[this.activeLang].name.trim() || !this.formData[this.activeLang].description.trim()) {
                return
            }
            const otherLang = this.activeLang === 'fr' ? 'en' : 'fr'
            if (!this.formData[otherLang].name.trim() || !this.formData[otherLang].description.trim()) {
                this.activeLang = otherLang
            }
        },

        // -- Load all users (no search filter) --
        async fetchAllUsers() {
            try {
                const response = await axios.get(`${this.$store.state.baseURL}/users/find`)
                return (response.data.users.data || []).map((u) => ({ id: u.id, name: u.name }))
            } catch (e) {
                return []
            }
        },

        // -- User search --
        onUserSearch(search, loading) {
            if (search.length) {
                loading(true)
                this.searchUser(search).then((response) => {
                    this.userOptions = response.data.users.data
                    loading(false)
                })
            }
        },

        // -- Field selection --
        onSelectField(payload) {
            this.selectedField = payload ? payload.field : null
        },
        onSelectStep() {
            this.selectedField = null
        },

        // -- Load existing form from DB --
        loadFormFromDB(form) {
            if (!form) return

            // General info
            const frTrans = (form.translations || []).find((t) => t.locale === 'fr') || form.fr || {}
            const enTrans = (form.translations || []).find((t) => t.locale === 'en') || form.en || {}
            this.formData.fr.name = frTrans.name || ''
            this.formData.fr.description = frTrans.description || ''
            this.formData.en.name = enTrans.name || ''
            this.formData.en.description = enTrans.description || ''

            // RACI — store all users for name resolution in categories
            if (form.racis && form.racis.length) {
                this.loadedFormUsers = form.racis.map((r) => ({ id: r.id, name: r.name }))
                this.racis.responsible = form.racis.filter((r) => r.is_responsible === 1)
                this.racis.accountable = form.racis.filter((r) => r.is_accountable === 1)
                this.racis.consulted = form.racis.filter((r) => r.is_consulted === 1)
                this.racis.informed = form.racis.filter((r) => r.is_informed === 1)
            }

            // Steps & attributes
            if (form.steps && form.steps.length) {
                this.formSteps = form.steps.map((step, index) => {
                    const stepFr = (step.translations || []).find((t) => t.locale === 'fr') || {}
                    return {
                        id: step.id,
                        name: stepFr.name || '',
                        content: stepFr.content || '',
                        order: index,
                        translations: step.translations,
                        attributes: (step.attributes || []).map((attr, ai) => {
                            const attrFr = (attr.translations || []).find((t) => t.locale === 'fr') || {}
                            const attrEn = (attr.translations || []).find((t) => t.locale === 'en') || {}
                            return {
                                id: attr.id,
                                form_attribute_type_id: attr.form_attribute_type_id,
                                label: attrFr.label || '',
                                order: ai,
                                translations: {
                                    fr: { label: attrFr.label || '', instruction: attrFr.instruction || '', description: attrFr.description || '', help: attrFr.help || '' },
                                    en: { label: attrEn.label || '', instruction: attrEn.instruction || '', description: attrEn.description || '', help: attrEn.help || '' }
                                },
                                config: attr.config || {},
                                requiredPublic: attr.requiredPublic || 0,
                                requiredBackoffice: attr.requiredBackoffice || 0,
                                hidden: attr.hidden || 0,
                                response_private: attr.response_private || 0
                            }
                        })
                    }
                })
            }
        },

        // -- Build RACI array for backend --
        buildRacis() {
            const racisMap = {}

            const addRole = (users, role) => {
                users.forEach((u) => {
                    if (!racisMap[u.id]) {
                        racisMap[u.id] = {
                            id: u.id,
                            name: u.name,
                            is_responsible: 0,
                            is_accountable: 0,
                            is_consulted: 0,
                            is_informed: 0
                        }
                    }
                    racisMap[u.id][role] = 1
                })
            }

            addRole(this.racis.responsible, 'is_responsible')
            addRole(this.racis.accountable, 'is_accountable')
            addRole(this.racis.consulted, 'is_consulted')
            addRole(this.racis.informed, 'is_informed')

            return Object.values(racisMap)
        },

        // -- Build default config per attribute type --
        buildAttrConfig(attr) {
            const typeId = attr.form_attribute_type_id || attr.typeId
            const existing = attr.config || {}

            // Text-based types (smalltext, textarea, markdown, number, email, date, datetime)
            if ([1, 3, 4, 5, 7, 8, 16].includes(typeId)) {
                const inputTypeMap = { 1: 'text', 3: 'textarea', 4: 'markdown', 5: 'number', 7: 'date', 8: 'datetime-local', 16: 'email' }
                return {
                    options: existing.options || { fr: [], en: [] },
                    type: existing.type || inputTypeMap[typeId] || 'text',
                    as: existing.as || [],
                    placeholder: existing.placeholder || { fr: [], en: [] }
                }
            }

            // Select, Radio, Checkbox
            if ([9, 10, 11].includes(typeId)) {
                return {
                    options: existing.options || { fr: [], en: [] },
                    placeholder: existing.placeholder || { fr: [], en: [] }
                }
            }

            // Category
            if (typeId === 13) {
                return {
                    options: existing.options || { fr: [], en: [] }
                }
            }

            // File upload
            if (typeId === 12) {
                return {
                    max: existing.max || 1,
                    min: existing.min || 0,
                    max_files: existing.max_files || null,
                    type: existing.type || 'files and medias',
                    'accepted-ext': existing['accepted-ext'] || []
                }
            }

            // Map / Location
            if (typeId === 6) {
                return existing.map ? existing : {
                    cardinality: null,
                    default_value: '',
                    map: {
                        config: {
                            center: [48.8566, 2.3522],
                            type: 'osm',
                            carte: 'city',
                            zoom: 13,
                            restriction: { city: '', country: '' }
                        },
                        pois: [],
                        restrict: { cities: [] }
                    }
                }
            }

            // GDPR
            if (typeId === 14) {
                return { options: existing.options || { fr: [], en: [] } }
            }

            // Audio
            if (typeId === 18) {
                return {
                    maxDuration: existing.maxDuration || 60,
                    format: existing.format || 'webm'
                }
            }

            // Hidden, Query, Rating, or unknown — at least return options
            return { options: existing.options || { fr: [], en: [] } }
        },

        // -- Build steps for backend --
        buildSteps() {
            return this.formSteps.map((step) => {
                const trans = step.translations || {}
                const stepFr = Array.isArray(trans)
                    ? (trans.find((t) => t.locale === 'fr') || {})
                    : (trans.fr || {})
                const stepEn = Array.isArray(trans)
                    ? (trans.find((t) => t.locale === 'en') || {})
                    : (trans.en || {})

                return {
                    id: typeof step.id === 'string' ? null : step.id || null,
                    fr: {
                        name: step.name || stepFr.name || '',
                        content: step.content || stepFr.content || ''
                    },
                    en: {
                        name: stepEn.name || step.name || '',
                        content: stepEn.content || step.content || ''
                    },
                    attributes: (step.attributes || []).map((attr) => {
                        const typeId = attr.form_attribute_type_id || attr.typeId
                        const attrTrans = attr.translations || {}
                        const frA = Array.isArray(attrTrans)
                            ? (attrTrans.find((t) => t.locale === 'fr') || {})
                            : (attrTrans.fr || {})
                        const enA = Array.isArray(attrTrans)
                            ? (attrTrans.find((t) => t.locale === 'en') || {})
                            : (attrTrans.en || {})

                        return {
                            id: typeof attr.id === 'string' ? null : attr.id || null,
                            form_attribute_type_id: typeId,
                            hidden: attr.hidden || 0,
                            private: attr.response_private || 0,
                            config: this.buildAttrConfig(attr),
                            fr: {
                                label: frA.label || '',
                                instruction: frA.instruction || '',
                                description: frA.description || '',
                                help: frA.help || ''
                            },
                            en: {
                                label: enA.label || '',
                                instruction: enA.instruction || '',
                                description: enA.description || '',
                                help: enA.help || ''
                            },
                            requiredPublic: attr.requiredPublic || 0,
                            requiredBackoffice: attr.requiredBackoffice || 0
                        }
                    })
                }
            })
        },

        // -- Save --
        async doSave() {
            this.loading = true

            // Build payload into store
            this.storeForm.fr = { name: this.formData.fr.name, description: this.formData.fr.description }
            this.storeForm.en = { name: this.formData.en.name, description: this.formData.en.description }
            this.storeForm.form_type_id = this.selectedFormTypeId
            this.storeForm.racis = this.buildRacis()
            this.storeForm.steps = this.buildSteps()

            try {
                let savedForm
                if (this.isEditMode) {
                    savedForm = await this.updateForm()
                } else {
                    savedForm = await this.createForm()
                }
                // Reload formSteps from backend response so attribute IDs are synced.
                // Without this, attributes with id:null get re-created on every subsequent save.
                if (savedForm) {
                    this.loadFormFromDB(savedForm)
                }
                this.saved = true
                this.hasChanges = false
            } catch (e) {
                console.error(e)
                this.$store.dispatch('dispatchError', e)
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.fb-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep { color: var(--v2-text-4); }
.tbv2-current { color: var(--v2-text-1); }

.fb-hero {
    margin-bottom: var(--v2-space-6);
    padding-bottom: var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border-subtle);
}

.fb-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.fb-back:hover {
    color: var(--v2-primary);
}

.fb-back i {
    font-size: 0.75rem;
}

.fb-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.fb-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.fb-wrapper {
    width: 100%;
    padding: 0;
}

@media (max-width: 900px) {
    .fb-v2 {
        padding: 16px 18px 32px;
    }

    .fb-hero-title {
        font-size: 1.375rem;
    }
}

/* -- Loading -- */
.fb-loading {
    display: flex;
    justify-content: center;
    padding: 48px 0;
}

/* -- Stepper -- */
.fb-stepper {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 40px;
    padding: 0 48px;
}

.fb-stepper-item {
    display: flex;
    align-items: center;
    cursor: pointer;
    position: relative;
    flex: 1;
}

.fb-stepper-item:last-child {
    flex: 0 0 auto;
}

.fb-stepper-item:hover .fb-stepper-icon {
    transform: scale(1.08);
}

.fb-stepper-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #94a3b8;
    font-size: 0.88rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
}

.fb-stepper-icon i {
    color: inherit;
}

.fb-stepper-text {
    display: flex;
    flex-direction: column;
    margin-left: 14px;
    white-space: nowrap;
}

.fb-stepper-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #94a3b8;
    transition: color 0.3s ease;
}

.fb-stepper-desc {
    font-size: 0.72rem;
    color: #cbd5e1;
    margin-top: 1px;
    transition: color 0.3s ease;
}

.fb-stepper-line {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    margin: 0 24px;
    align-self: center;
    border-radius: 2px;
    transition: background 0.4s ease;
}

/* Active */
.fb-stepper-item.is-active .fb-stepper-icon {
    background: var(--theme-primary, #3b82f6);
    color: #fff;
    border-color: rgba(59, 130, 246, 0.25);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.fb-stepper-item.is-active .fb-stepper-label {
    color: #0f172a;
}

.fb-stepper-item.is-active .fb-stepper-desc {
    color: #64748b;
}

/* Done */
.fb-stepper-item.is-done .fb-stepper-icon {
    background: #10b981;
    color: #fff;
    border-color: rgba(16, 185, 129, 0.2);
}

.fb-stepper-item.is-done .fb-stepper-label {
    color: #475569;
}

.fb-stepper-item.is-done .fb-stepper-desc {
    color: #94a3b8;
}

.fb-stepper-item.is-done .fb-stepper-line {
    background: #10b981;
}

/* -- Content card -- */
.fb-content {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    min-height: 420px;
    padding: 40px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.02);
}

.fb-panel {
    text-align: left;
}

/* -- Navigation -- */
.fb-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 28px;
}

.fb-btn {
    display: inline-flex;
    align-items: center;
    padding: 11px 28px;
    border: none;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fb-btn:active {
    transform: scale(0.97);
}

.fb-btn--primary {
    background: var(--theme-primary, #3b82f6);
    color: #fff;
}

.fb-btn--primary i {
    color: #fff;
}

.fb-btn--primary:hover {
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
    transform: translateY(-1px);
}

.fb-btn--secondary {
    background: #f8fafc;
    color: #475569;
    border: 1.5px solid #e2e8f0;
}

.fb-btn--secondary:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
}

.fb-btn--success {
    background: #10b981;
    color: #fff;
}

.fb-btn--success i {
    color: #fff;
}

.fb-btn--success:hover {
    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
    transform: translateY(-1px);
}

/* -- Step 1 layout -- */
.fb-step1-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
}

.fb-step1-left {
    padding-right: 48px;
    border-right: 1px solid #f1f5f9;
}

.fb-section-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 28px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}

.fb-section-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
    color: var(--theme-primary, #3b82f6);
}

.fb-section-icon i {
    color: inherit;
}

.fb-section-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
}

.fb-section-desc {
    font-size: 0.78rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.5;
}

/* -- Lang tabs -- */
.fb-lang-tabs {
    display: flex;
    gap: 4px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 4px;
    margin-bottom: 28px;
    width: fit-content;
}

.fb-lang-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 22px;
    border: none;
    background: transparent;
    border-radius: 9px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fb-lang-tab:hover:not(.is-active) {
    color: #64748b;
    background: rgba(0, 0, 0, 0.02);
}

.fb-lang-tab.is-active {
    background: #fff;
    color: var(--theme-primary, #3b82f6);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.03);
}

.fb-lang-flag {
    font-size: 1.05rem;
    line-height: 1;
}

/* -- Form fields -- */
.fb-fields {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.fb-field {
    display: flex;
    flex-direction: column;
}

.fb-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 7px;
    letter-spacing: 0.01em;
}

.fb-label i {
    color: #94a3b8;
}

.fb-required {
    color: #ef4444;
    font-weight: 400;
}

.fb-input,
.fb-textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.88rem;
    color: #0f172a;
    outline: none;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;
}

.fb-input::placeholder,
.fb-textarea::placeholder {
    color: #cbd5e1;
}

.fb-input:hover,
.fb-textarea:hover {
    border-color: #cbd5e1;
}

.fb-input:focus,
.fb-textarea:focus {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
}

.fb-textarea {
    resize: vertical;
    min-height: 120px;
    line-height: 1.6;
}

/* Validation */
.fb-field.has-error .fb-input,
.fb-field.has-error .fb-textarea {
    border-color: #ef4444;
    background: #fef2f2;
}

.fb-field.has-error .fb-input:focus,
.fb-field.has-error .fb-textarea:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08) !important;
}

.fb-error {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #ef4444;
    margin-top: 6px;
}

/* -- Type select (step 1) -- */
.fb-type-select {
    max-width: 800px;
    margin: 0 auto;
}

.fb-type-empty {
    text-align: center;
    padding: 48px;
    color: #94a3b8;
    font-size: 1.2rem;
}

.fb-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;
}

.fb-type-card {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px;
    border: 2px solid #f1f5f9;
    border-radius: 14px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fb-type-card:hover {
    border-color: #bfdbfe;
    box-shadow: 0 2px 10px rgba(59, 130, 246, 0.08);
}

.fb-type-card.is-selected {
    border-color: var(--theme-primary, #3b82f6);
    background: #f0f7ff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.fb-type-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s ease;
}

.fb-type-card.is-selected .fb-type-card-icon {
    background: #dbeafe;
}

.fb-type-card-icon i {
    font-size: 1rem;
    color: var(--theme-primary, #3b82f6);
}

.fb-type-card-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.fb-type-card-name {
    font-size: 0.88rem;
    font-weight: 600;
    color: #0f172a;
}

.fb-type-card-desc {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 2px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.fb-type-card-check {
    flex-shrink: 0;
}

.fb-type-card-check i {
    font-size: 1.1rem;
    color: var(--theme-primary, #3b82f6);
}

.fb-content--builder {
    padding: 0;
    overflow: hidden;
}

/* -- Builder layout (step 2) -- */
.fb-builder-layout {
    display: flex;
    height: 65vh;
    overflow: hidden;
}

/* -- RACI cards -- */
.fb-raci-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.fb-raci-card {
    border-radius: 12px;
    padding: 16px;
    border: 1.5px solid #e2e8f0;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.fb-raci-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.fb-raci-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.fb-raci-card-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    flex-shrink: 0;
}

.fb-raci-card-icon i {
    color: inherit;
}

.fb-raci-card-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
}

/* Color variants */
.fb-raci--blue { border-color: #dbeafe; }
.fb-raci--blue:hover { border-color: #93c5fd; }
.fb-raci--blue .fb-raci-card-icon { background: #eff6ff; color: #3b82f6; }

.fb-raci--purple { border-color: #e9d5ff; }
.fb-raci--purple:hover { border-color: #c084fc; }
.fb-raci--purple .fb-raci-card-icon { background: #faf5ff; color: #8b5cf6; }

.fb-raci--amber { border-color: #fef3c7; }
.fb-raci--amber:hover { border-color: #fbbf24; }
.fb-raci--amber .fb-raci-card-icon { background: #fffbeb; color: #d97706; }

.fb-raci--teal { border-color: #ccfbf1; }
.fb-raci--teal:hover { border-color: #5eead4; }
.fb-raci--teal .fb-raci-card-icon { background: #f0fdfa; color: #0d9488; }

/* -- v-select overrides -- */
.fb-vselect .vs__dropdown-toggle {
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 3px 6px;
    min-height: 38px;
    background: #f8fafc;
    transition: all 0.2s ease;
}

.fb-vselect .vs__dropdown-toggle:hover {
    border-color: #cbd5e1;
}

.fb-vselect .vs__dropdown-toggle:focus-within {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
    background: #fff;
}

.fb-vselect .vs__selected {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    color: #1e40af;
    font-size: 0.78rem;
    padding: 2px 8px;
    margin: 2px;
}

.fb-vselect .vs__deselect {
    color: #93c5fd;
}

.fb-vselect .vs__search {
    font-size: 0.82rem;
}

.fb-vselect .vs__search::placeholder {
    color: #cbd5e1;
}

/* -- Summary (step 3) -- */
.fb-summary {
    max-width: 860px;
    margin: 0 auto;
}

.fb-summary-header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 32px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}

.fb-summary-header-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.04));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.fb-summary-header-icon i {
    font-size: 1.1rem;
    color: #10b981;
}

.fb-summary-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.fb-summary-subtitle {
    font-size: 0.82rem;
    color: #64748b;
    margin: 4px 0 0;
}

.fb-summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.fb-summary-card {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 14px;
    overflow: hidden;
}

.fb-summary-card--full {
    grid-column: 1 / -1;
}

.fb-summary-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.85rem;
    font-weight: 600;
    color: #334155;
}

.fb-summary-card-header i {
    color: var(--theme-primary, #3b82f6);
    font-size: 0.9rem;
}

.fb-summary-badge {
    margin-left: auto;
    font-size: 0.72rem;
    font-weight: 600;
    background: #eff6ff;
    color: var(--theme-primary, #3b82f6);
    padding: 3px 10px;
    border-radius: 20px;
}

.fb-summary-card-body {
    padding: 16px 18px;
}

.fb-summary-card-empty {
    padding: 24px 18px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.82rem;
}

.fb-summary-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 8px 0;
    gap: 16px;
}

.fb-summary-row + .fb-summary-row {
    border-top: 1px solid #f1f5f9;
}

.fb-summary-label {
    font-size: 0.78rem;
    color: #64748b;
    flex-shrink: 0;
    font-weight: 500;
}

.fb-summary-value {
    font-size: 0.82rem;
    color: #0f172a;
    font-weight: 500;
    text-align: right;
}

.fb-summary-value--truncate {
    max-width: 260px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fb-summary-value--html {
    max-width: 300px;
    font-size: 0.8rem;
    line-height: 1.5;
    color: #334155;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}

.fb-summary-empty {
    color: #cbd5e1;
}

.fb-summary-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: flex-end;
}

.fb-summary-pill {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 6px;
}

.fb-pill--blue { background: #eff6ff; color: #1e40af; }
.fb-pill--purple { background: #faf5ff; color: #7c3aed; }
.fb-pill--amber { background: #fffbeb; color: #b45309; }
.fb-pill--teal { background: #f0fdfa; color: #0f766e; }

/* Steps summary */
.fb-summary-step {
    padding: 12px 0;
}

.fb-summary-step + .fb-summary-step {
    border-top: 1px solid #f1f5f9;
}

.fb-summary-step-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.fb-summary-step-num {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: var(--theme-primary, #3b82f6);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.fb-summary-step-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #0f172a;
    flex: 1;
}

.fb-summary-step-count {
    font-size: 0.72rem;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 20px;
}

.fb-summary-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-left: 34px;
}

.fb-summary-field-chip {
    font-size: 0.75rem;
    font-weight: 500;
    color: #475569;
    background: #fff;
    border: 1px solid #e5e7eb;
    padding: 4px 12px;
    border-radius: 8px;
}

/* -- Success state -- */
.fb-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    text-align: center;
}

.fb-success-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #d1fae5, #ecfdf5);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 4px 20px rgba(16, 185, 129, 0.15);
    animation: fb-success-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fb-success-icon i {
    font-size: 2rem;
    color: #10b981;
}

@keyframes fb-success-pop {
    0% { transform: scale(0.5); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
}

.fb-success-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

/* -- Transition -- */
.slide-fade-enter-active {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
    transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
}
</style>
